import { useState, useEffect } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Receipt, Download, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../config';


const labels = {
  en: { title: 'Invoices', empty: 'No invoices yet', download: 'Download', date: 'Date', amount: 'Amount', package: 'Package', status: 'Status', paid: 'Paid' },
  fr: { title: 'Factures', empty: 'Aucune facture', download: 'Telecharger', date: 'Date', amount: 'Montant', package: 'Offre', status: 'Statut', paid: 'Paye' },
  es: { title: 'Facturas', empty: 'Sin facturas', download: 'Descargar', date: 'Fecha', amount: 'Importe', package: 'Plan', status: 'Estado', paid: 'Pagado' },
  de: { title: 'Rechnungen', empty: 'Keine Rechnungen', download: 'Herunterladen', date: 'Datum', amount: 'Betrag', package: 'Paket', status: 'Status', paid: 'Bezahlt' },
  hi: { title: 'चालान', empty: 'अभी कोई चालान नहीं', download: 'डाउनलोड', date: 'तारीख', amount: 'राशि', package: 'पैकेज', status: 'स्थिति', paid: 'भुगतान किया' },
};

const formatDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
};

const formatAmount = (amount, currency, lang) =>
  new Intl.NumberFormat(lang === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: (currency || 'eur').toUpperCase(), minimumFractionDigits: 2 }).format(amount);

const generateInvoiceHTML = (invoice, lang) => {
  const l = labels[lang] || labels.en;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice ${invoice.id}</title>
<style>body{font-family:Arial,sans-serif;max-width:700px;margin:40px auto;padding:20px;color:#333}
.header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #3b82f6;padding-bottom:20px;margin-bottom:30px}
.logo{font-size:24px;font-weight:bold;color:#3b82f6}
.invoice-id{color:#666;font-size:14px}
table{width:100%;border-collapse:collapse;margin:20px 0}
th{text-align:left;padding:10px;background:#f3f4f6;border:1px solid #e5e7eb;font-size:13px}
td{padding:10px;border:1px solid #e5e7eb;font-size:14px}
.total{font-size:20px;font-weight:bold;text-align:right;margin-top:20px}
.footer{margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;font-size:12px;color:#888;text-align:center}
@media print{body{margin:0}}</style></head>
<body>
<div class="header"><div class="logo">Hexo<span style="color:#3b82f6">PDF</span></div><div class="invoice-id">Invoice #${invoice.id.slice(0,8).toUpperCase()}<br>${formatDate(invoice.date, lang)}</div></div>
<table><tr><th>${l.package}</th><th>${l.amount}</th><th>${l.status}</th></tr>
<tr><td>${invoice.package_name}</td><td>${formatAmount(invoice.amount, invoice.currency, lang)}</td><td>${l.paid}</td></tr></table>
<div class="total">Total: ${formatAmount(invoice.amount, invoice.currency, lang)}</div>
<div class="footer">Hexoworks &middot; Invoice generated from HexoPDF<br>hexo-works.com</div>
</body></html>`;
};

export const BillingSection = () => {
  const { lang } = useI18n();
  const { getToken } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const l = labels[lang] || labels.en;

  useEffect(() => {
    const fetch = async () => {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/billing/invoices`, { headers: { Authorization: `Bearer ${token}` } });
        setInvoices(res.data.invoices || []);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [getToken]);

  const handleDownload = (invoice) => {
    const html = generateInvoiceHTML(invoice, lang);
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HexoPDF_Invoice_${invoice.id.slice(0,8).toUpperCase()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return null;

  return (
    <div className="p-6 rounded-2xl bg-card border border-border mb-6" data-testid="billing-section">
      <div className="flex items-center gap-2 mb-4">
        <Receipt className="w-4 h-4 text-blue-500" />
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium">{l.title}</h2>
      </div>
      {invoices.length === 0 ? (
        <div className="flex items-center gap-3 text-muted-foreground text-sm py-4" data-testid="billing-empty">
          <FileText className="w-5 h-5" />
          {l.empty}
        </div>
      ) : (
        <div className="space-y-3" data-testid="billing-list">
          {invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary" data-testid={`invoice-${inv.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-foreground text-sm font-medium truncate">{inv.package_name}</p>
                <p className="text-muted-foreground text-xs">{formatDate(inv.date, lang)} &middot; {formatAmount(inv.amount, inv.currency, lang)}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDownload(inv)} className="text-blue-500 hover:text-blue-400 shrink-0" data-testid={`invoice-download-${inv.id}`}>
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline text-xs">{l.download}</span>
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
